import { schema } from 'normalizr';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { RSAA, createAction } from 'redux-api-middleware';
import { handleResponse, handleError, buildQueryString } from '../utils';
import { getToken, getAccessToken } from '../utils/auth';

export const FETCH_USERS_REQUEST = 'FETCH_USERS_REQUEST';
export const FETCH_USERS_SUCCESS = 'FETCH_USERS_SUCCESS';
export const FETCH_USERS_FAILURE = 'FETCH_USERS_FAILURE';
export const FETCH_USER_REQUEST = 'FETCH_USER_REQUEST';
export const FETCH_USER_SUCCESS = 'FETCH_USER_SUCCESS';
export const FETCH_USER_FAILURE = 'FETCH_USER_FAILURE';
export const EDIT_USER_REQUEST = 'EDIT_USER_REQUEST';
export const EDIT_USER_SUCCESS = 'EDIT_USER_SUCCESS';
export const EDIT_USER_FAILURE = 'EDIT_USER_FAILURE';
export const CREATE_USER_REQUEST = 'CREATE_USER_REQUEST';
export const CREATE_USER_SUCCESS = 'CREATE_USER_SUCCESS';
export const CREATE_USER_FAILURE = 'CREATE_USER_FAILURE';
export const CREATE_UPLOAD_USERS_REQUEST = 'CREATE_UPLOAD_USERS_REQUEST';
export const CREATE_UPLOAD_USERS_SUCCESS = 'CREATE_UPLOAD_USERS_SUCCESS';
export const CREATE_UPLOAD_USERS_FAILURE = 'CREATE_UPLOAD_USERS_FAILURE';
export const DELETE_USER_REQUEST = 'DELETE_USER_REQUEST';
export const DELETE_USER_SUCCESS = 'DELETE_USER_SUCCESS';
export const DELETE_USER_FAILURE = 'DELETE_USER_FAILURE';

export const usersSchema = new schema.Entity('users', {}, { idAttribute: 'oid' });
export const royaltyUserSchema = new schema.Entity('users', {}, { idAttribute: 'oid' });

const userHasRole = (user, role) => {
  const slugs = user.secondaryRoles?.map(secondaryRole => secondaryRole.slug) || [];
  slugs.push(user.role);

  return !!slugs.find(slug => role === slug);
};

export const useUserSelectorByRoles = roles =>
  useSelector(state =>
    Object.values(get(state, 'entities.users', {}))
      .filter(user => user.displayName && user.oid)
      .filter(user => roles.some(role => userHasRole(user, role))),
  );

export const fetchUsers = filters => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/users?${buildQueryString({
      start: filters.start || 0,
      count: filters.count || 20,
      role: filters.role,
    })}${
      filters.secondaryRoles?.length > 0
        ? filters.secondaryRoles?.map(secondaryRole => `&secondaryRole=${secondaryRole}`).join('&')
        : ''
    }`,
    method: 'GET',
    types: [
      { type: FETCH_USERS_REQUEST },
      {
        type: FETCH_USERS_SUCCESS,
        payload: (action, state, res) => handleResponse([usersSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: FETCH_USERS_FAILURE, payload: handleError },
    ],
  },
});

/**
 * Fetch users based on secondaryRoles.
 *
 * @param {Array} secondaryRoles
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchUsersByRoles = secondaryRoles => {
  const secondaryRoleParams = secondaryRoles
    .map(secondaryRole => `secondaryRole=${secondaryRole}`)
    .join('&');

  return createAction({
    endpoint: `${window.config.apiUrl}/users?${secondaryRoleParams}&count=100`,
    method: 'GET',
    types: [
      { type: FETCH_USERS_REQUEST },
      {
        type: FETCH_USERS_SUCCESS,
        payload: (action, state, res) => handleResponse([usersSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: FETCH_USERS_FAILURE, payload: handleError },
    ],
  });
};

/**
 * Fetch user by oid
 * @param oid - User's oid
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchUser = oid => {
  return createAction({
    endpoint: `${window.config.apiUrl}/users/${oid}`,
    method: 'GET',
    types: [
      { type: FETCH_USER_REQUEST },
      {
        type: FETCH_USER_SUCCESS,
        payload: (action, state, res) => handleResponse(usersSchema, action, state, res),
      },
      { type: FETCH_USER_FAILURE, payload: handleError },
    ],
  });
};

/**
 * Updated an existing user
 * @param values - Object containing user form values
 * @param oid - User's oid
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const editUser = (values, oid) =>
  createAction({
    endpoint: `${window.config.apiUrl}/users/${oid}`,
    method: 'PATCH',
    body: JSON.stringify(values),
    types: [
      { type: EDIT_USER_REQUEST },
      {
        type: EDIT_USER_SUCCESS,
        payload: (action, state, res) => handleResponse(usersSchema, action, state, res),
      },
      {
        type: EDIT_USER_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * create a new user
 * @param values - Object containing user form values
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const createUser = values =>
  createAction({
    endpoint: `${window.config.apiUrl}/users`,
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      DelegatedAccessToken: `${getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(values),
    types: [
      { type: CREATE_USER_REQUEST },
      {
        type: CREATE_USER_SUCCESS,
        payload: (action, state, res) => handleResponse(usersSchema, action, state, res),
      },
      {
        type: CREATE_USER_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * create a set users from an imported csv file
 * @param values - Object containing users form csfv file
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const uploadUsers = values =>
  createAction({
    endpoint: `${window.config.apiUrl}/users/upload`,
    method: 'POST',
    body: JSON.stringify(values),
    types: [
      { type: CREATE_UPLOAD_USERS_REQUEST },
      {
        type: CREATE_UPLOAD_USERS_SUCCESS,
        payload: (action, state, res) => handleResponse(action, state, res),
      },
      {
        type: CREATE_UPLOAD_USERS_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Remove a user
 * @param values - Object containing user form values
 * @param oid - User's oid
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const deleteUser = (oid, role) =>
  createAction({
    endpoint: `${window.config.apiUrl}/users/${oid}`,
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      DelegatedAccessToken: `${getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ oid, role }),
    types: [
      { type: DELETE_USER_REQUEST },
      { type: DELETE_USER_SUCCESS },
      { type: DELETE_USER_FAILURE, payload: handleError },
    ],
  });
